// @ts-nocheck
"use client"

import { Badge } from "@repo/ui/components/ui/badge"
import { cn } from "@repo/ui/lib/utils"
import { motion, useInView, useScroll, useTransform } from "framer-motion"
import { Smartphone, TreePine, Wrench } from "lucide-react"
import { useRef } from "react"

interface ScreenshotsSectionProps {
  content?: Record<string, string>
}

interface PaperImage {
  src: string
  alt: string
}

interface PaperStackProps {
  backImage: PaperImage
  frontImage: PaperImage
}

function PaperStack({ backImage, frontImage }: PaperStackProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  })
  const frontY = useTransform(scrollYProgress, [0, 1], [-16, 16])
  const backY = useTransform(scrollYProgress, [0, 1], [16, -16])

  return (
    <div
      ref={containerRef}
      className="relative mx-auto aspect-[4/5] w-full max-w-[300px]"
    >
      <motion.div
        style={{ y: backY }}
        className="absolute right-0 top-4 z-10 w-[85%] -rotate-[7deg] overflow-hidden rounded-2xl border border-border/70 bg-card shadow-forest-lg ring-1 ring-black/5"
      >
        <img
          src={backImage.src}
          alt={backImage.alt}
          className="block size-full object-contain object-top"
          loading="lazy"
        />
      </motion.div>
      <motion.div
        style={{ y: frontY }}
        className={cn(
          "absolute bottom-0 left-0 z-20 w-[85%] overflow-hidden rounded-2xl border border-border/70 bg-card shadow-forest-xl ring-1 ring-black/5",
          "rotate-[7deg]",
        )}
      >
        <img
          src={frontImage.src}
          alt={frontImage.alt}
          className="block size-full object-contain object-top"
          loading="lazy"
        />
      </motion.div>
    </div>
  )
}

interface FeatureBlurbProps {
  badge: string
  title: string
  description: string
  icon: typeof TreePine
  delay?: number
}

function FeatureBlurb({
  badge,
  title,
  description,
  icon: Icon,
  delay = 0,
}: FeatureBlurbProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-60px" })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className="flex flex-col gap-3"
    >
      <Badge variant="secondary" className="w-fit gap-1.5">
        <Icon className="size-3.5" aria-hidden />
        {badge}
      </Badge>
      <h3 className="text-xl font-semibold text-foreground sm:text-2xl tracking-tight">
        {title}
      </h3>
      <p className="text-muted-foreground leading-[1.7]">{description}</p>
    </motion.div>
  )
}

export function ScreenshotsSection({
  content = {},
}: ScreenshotsSectionProps): JSX.Element {
  const headerRef = useRef<HTMLDivElement>(null)
  const isHeaderInView = useInView(headerRef, { once: true, margin: "-80px" })

  return (
    <section
      id="screenshots"
      className="overflow-hidden bg-background py-20 sm:py-28"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* ─── Section header ──────────────────────────────────────────── */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 24 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mx-auto mb-16 flex max-w-2xl flex-col items-center gap-4 text-center sm:mb-20"
        >
          <Badge variant="outline" className="gap-1.5">
            <Smartphone className="size-3.5" aria-hidden />
            {content["screenshots.badge"] || "Interface terrain"}
          </Badge>
          <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            {content["screenshots.title"] || "Votre outil terrain"}
            <span className="text-primary block">au quotidien</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-[1.7]">
            {content["screenshots.description"] ||
              "Consultez et gérez vos fiches arbre et interventions directement sur le terrain, avec une interface claire et intuitive."}
          </p>
        </motion.div>

        {/* ─── Screenshot stacks ──────────────────────────────────────── */}
        <div className="grid items-start gap-16 lg:grid-cols-2 lg:gap-20">
          <div className="flex flex-col gap-10">
            <PaperStack
              backImage={{
                src: "/images/fiche/fiche-2.png",
                alt: "Fiche arbre — vue détaillée",
              }}
              frontImage={{
                src: "/images/fiche/fiche.png",
                alt: "Fiche arbre — vue principale",
              }}
            />
            <FeatureBlurb
              badge={content["screenshots.fiche.badge"] || "Fiche arbre"}
              title={
                content["screenshots.fiche.title"] ||
                "Toutes les informations en un coup d'œil"
              }
              description={
                content["screenshots.fiche.description"] ||
                "Accédez à l'état sanitaire, la localisation, les diagnostics et l'historique de chaque arbre depuis une fiche détaillée et ergonomique."
              }
              icon={TreePine}
              delay={0.1}
            />
          </div>

          <div className="flex flex-col gap-10">
            <PaperStack
              backImage={{
                src: "/images/fiche/intervention-2.png",
                alt: "Intervention — suivi des travaux",
              }}
              frontImage={{
                src: "/images/fiche/intervention.png",
                alt: "Intervention — fiche principale",
              }}
            />
            <FeatureBlurb
              badge={content["screenshots.intervention.badge"] || "Intervention"}
              title={
                content["screenshots.intervention.title"] ||
                "Planifiez et suivez vos interventions"
              }
              description={
                content["screenshots.intervention.description"] ||
                "Créez, assignez et suivez les travaux d'élagage et de maintenance avec un suivi en temps réel sur le terrain."
              }
              icon={Wrench}
              delay={0.2}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
